import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Waves } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";

const LS_KEY = "erc_chat_sid";

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  useEffect(() => {
    if (!open || !sid) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/chat/${sid}`);
        if (active) setMessages(data.messages || []);
      } catch (e) {
        if (e.response?.status === 404) { localStorage.removeItem(LS_KEY); setSid(""); }
      }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { active = false; clearInterval(t); };
  }, [open, sid]);

  useEffect(() => { if (open) scrollDown(); }, [messages, open]);

  const startChat = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/chat/start", { name: form.name, email: form.email });
      localStorage.setItem(LS_KEY, data.session_id);
      setSid(data.session_id);
      setMessages([]);
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !sid) return;
    setText("");
    setMessages((m) => [...m, { id: "tmp" + Date.now(), sender: "visitor", text: t, at: new Date().toISOString() }]);
    scrollDown();
    try { await api.post(`/chat/${sid}/message`, { text: t }); } catch (e) { /* keep optimistic */ }
  };

  return (
    <>
      {/* Launcher */}
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with us"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-cyan text-void shadow-[0_8px_30px_rgba(0,240,255,0.35)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className="fixed bottom-24 right-5 z-[60] w-[min(92vw,380px)] h-[min(70vh,560px)] rounded-2xl overflow-hidden flex flex-col border border-cyan/25 bg-[#0a1424] shadow-2xl"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-cyan/15 to-transparent flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-cyan/20 flex items-center justify-center text-cyan"><Waves size={18} /></div>
              <div>
                <p className="font-display text-lg leading-none">Chat with us</p>
                <p className="text-white/40 text-xs font-mono mt-1">Corals · Fish · Services</p>
              </div>
            </div>

            {!sid ? (
              /* Intro form */
              <form onSubmit={startChat} className="flex-1 flex flex-col justify-center gap-3 p-6" data-testid="chat-start-form">
                <p className="text-white/60 text-sm mb-1">👋 Hi! Ask us anything about our corals, fish, or aquarium services. Leave your details and we'll reply here.</p>
                <input data-testid="chat-name-input" className="er-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="chat-email-input" type="email" className="er-input" placeholder="Email (so we can follow up)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <button data-testid="chat-start-btn" type="submit" disabled={busy} className="btn-pill btn-solid w-full disabled:opacity-50">{busy ? "Starting…" : "Start chatting"}</button>
              </form>
            ) : (
              <>
                {/* Thread */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-thread">
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-white/10 text-white/80 px-4 py-2.5 text-sm">
                      Welcome to Exotic Reefing Inc! 🐠 How can we help you today?
                    </div>
                  </div>
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-4 py-2.5 text-sm rounded-2xl ${m.sender === "visitor" ? "rounded-tr-sm bg-cyan text-void" : "rounded-tl-sm bg-white/10 text-white/85"}`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                {/* Composer */}
                <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
                  <input data-testid="chat-message-input" className="er-input flex-1 !py-2.5" placeholder="Type your message…" value={text} onChange={(e) => setText(e.target.value)} />
                  <button data-testid="chat-send-btn" type="submit" aria-label="Send" className="w-11 h-11 shrink-0 rounded-full bg-cyan text-void flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"><Send size={17} /></button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
